import * as Sentry from "@sentry/react";
import { RefreshCcw, TriangleAlert } from "lucide-react";
import { Suspense, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { TranslationFeKey } from "shared/types/translation-key";

import { OnMessageSendFunction } from "../../chat/conversation";
import { getComponent } from "../lookup";
import { SocketAssistantMessage } from "../schema-socket";
import { MessageRecordWithType } from "~/api/generated/multiagent";
import { Text } from "~/components/text";
import { useT } from "~/i18n/use-t";
import { isLocalhost } from "~/util/env-utils";

export type MessageProcessedChoices = MessageRecordWithType & {
  questionNodeInfo: Omit<
    MessageRecordWithType["questionNodeInfo"],
    "choices"
  > & {
    choices: TranslationFeKey[] | undefined;
  };
};

const processChoices = (
  message: SocketAssistantMessage,
): MessageProcessedChoices => {
  const clone = structuredClone(message);

  const processedChoices: TranslationFeKey[] | undefined =
    clone.questionNodeInfo.choices?.reduce((processed, choice) => {
      processed.push(
        `graph.choice.${clone.questionNodeInfo.subtype}.${choice}` as TranslationFeKey,
      );
      return processed;
    }, [] as TranslationFeKey[]);

  clone.questionNodeInfo.choices = processedChoices;

  return clone as MessageProcessedChoices;
};

const FallbackLoadingComponent: React.FC<{ showAfterMs?: number }> = ({
  showAfterMs = 75,
}) => {
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldShow(true);
    }, showAfterMs);

    return () => clearTimeout(timer);
  }, [showAfterMs]);

  return shouldShow ? (
    <div className="skeleton h-60 w-full sm:w-[30rem]" />
  ) : null;
};

const ErrorFallback: React.FC = () => {
  const t = useT();

  return (
    <div className="card w-full border bg-white shadow-lg sm:w-[30rem]">
      <div className="card-body flex flex-col items-center text-center">
        <TriangleAlert size={42} className="text-neutral-900" />

        <Text
          tx="graph.errors.failedToLoadNode.explanation"
          className="mt-4 text-neutral-900"
        />

        <button
          title={t({ tx: "graph.errors.failedToLoadNode.cta" })}
          onClick={() => window.location.reload()}
          type="button"
          className="btn-wide btn mt-8"
        >
          <RefreshCcw width={16} />
          <Text tx="graph.errors.failedToLoadNode.cta" />
        </button>
      </div>
    </div>
  );
};

export const MessageComponent: React.FC<{
  message: SocketAssistantMessage;
  sendMessage: OnMessageSendFunction;
}> = ({ message, sendMessage }) => {
  const { type, subtype } = message.questionNodeInfo;

  const [Component, validationFunction] = getComponent(type, subtype);
  if (!Component) {
    Sentry.captureException(new Error("invalid lookup configuration"), {
      level: "error",
      extra: { type, subtype },
    });
    return <div>{"Component not found"}</div>;
  }

  const processedMessage = processChoices(message);

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error: Error) => {
        if (isLocalhost) {
          console.error(error);
        } else {
          Sentry.captureException(error, {
            level: "error",
            extra: { type, subtype },
          });
        }
      }}
    >
      <Suspense fallback={<FallbackLoadingComponent />}>
        <Component
          message={processedMessage}
          sendMessage={sendMessage}
          validationFunction={validationFunction}
        />
      </Suspense>
    </ErrorBoundary>
  );
};
